import React, { FC, useState } from "react";

import log from "loglevel";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import { useMount } from "react-use";
import { Button } from "@mui/material";

import { ModalDialog } from "@/components/molecules";
import { greenCardApi } from "@/shared/apis";
import { InsuredGood } from "@/shared/domain";
import { useAuth } from "@/shared/hooks";
import * as tagManager from "@/tagManager";

import { InsuredGoodRootCategory } from "../../InsuredGoods/utils/InsuredGoodRootCategory";
import GreenCardRequestError from "../GreenCardRequestError/GreenCardRequestError";
import GreenCardRequestSuccessInfo from "../GreenCardRequestSuccessInfo/GreenCardRequestSuccessInfo";
import { StyledGreenCardRequest } from "./styled";

type GreenCardRequestModalProps = {
    onClose: () => void;
    insuredGood: InsuredGood;
};

const GreenCardRequestModal: FC<GreenCardRequestModalProps> = ({ insuredGood, onClose }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [requestSuccessType, setRequestSuccessType] = useState(null);
    const [showErrorInfo, setShowErrorInfo] = useState(false);

    const { mutate: requestGreenCard, isLoading } = useMutation(greenCardApi.requestGreenCard, {
        // Type need be fixed on Swagger Responses.$200
        onSuccess: (e: any) => {
            setRequestSuccessType(e.message);
            // Tag Manager
            tagManager.pushInsuranceCardRequestSentEvent();
        },
        onError: () => {
            tagManager.pushInsuranceCardRequestSentEvent(false);
            log.info("failed to request green card");
            setShowErrorInfo(true);
        },
        useErrorBoundary: false,
    });

    useMount(() => tagManager.pushInsuranceCardRequestBeginEvent());

    const handleRequestGreenCard = () => {
        requestGreenCard({
            vehicleId: insuredGood.id,
            params: {
                policyholderId: insuredGood.policyholderId,
                ibpAclId: user.getAccessRightFor(insuredGood.policyholderId).accessControlListId,
            },
        });
    };

    return (
        <ModalDialog fullSize open title={t("GreenCardRequest.Title")} onClose={onClose}>
            <>
                {showErrorInfo && <GreenCardRequestError onContinue={onClose} />}
                {!!requestSuccessType && (
                    <GreenCardRequestSuccessInfo onSuccess={onClose} successResponse={requestSuccessType} />
                )}
                {!showErrorInfo && !requestSuccessType && (
                    <StyledGreenCardRequest>
                        <div className="greenCardTitle">{t("GreenCardRequest.ChooseVehicleDetail")}</div>
                        <div className="vehicleCard" data-testid={`green_card-item-${insuredGood?.title}`}>
                            <div className="vehicleInf">{`${t("Common.Vehicle")} - ${insuredGood?.title}`}</div>
                            <div className="policyholderInf">
                                {`${t("Common.Policyholder")} - ${insuredGood?.policyHolder}`}
                            </div>
                            <div className="iconGoodsCategory">
                                {InsuredGoodRootCategory.getFromId(insuredGood.category.id).categoryIcon}
                            </div>
                        </div>
                        <div className="btnWrapper d-flex">
                            <Button
                                data-testid="button_confirm"
                                disabled={isLoading}
                                fullWidth
                                onClick={handleRequestGreenCard}
                            >
                                {t("Common.Confirm")}
                            </Button>
                        </div>
                    </StyledGreenCardRequest>
                )}
            </>
        </ModalDialog>
    );
};

export default GreenCardRequestModal;
